import React, { useState } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  ArrowEnterLeftRegular,
  ArrowSyncRegular,
  DeleteRegular,
  ReOrderDotsHorizontalRegular,
} from "@fluentui/react-icons";
import {
  Card,
  Field,
  Input,
  Tooltip,
  Button,
  CardProps,
  mergeClasses,
  useRestoreFocusTarget,
} from "@fluentui/react-components";

import { Stack, StackProps } from "@/components/stack";
import { AutosizeTextarea } from "@/components/autosize-textarea";
import { useFallbackRuleCardStyles } from "./styles";

import { DeleteFallbackRuleDialog } from "../delete-fallback-rule-dialog";

import { FallbackRule } from "@/types/risk-analysis";

interface FallbackRuleCardProps extends CardProps {
  fallbackRule: FallbackRule;
  onEdit: (field: string, value: string) => void;
  onSwap: () => void;
  onDelete: () => void;
}

export function FallbackRuleCard({
  id,
  fallbackRule,
  className,
  style,
  onEdit,
  onDelete,
  onSwap,
  ...props
}: FallbackRuleCardProps) {
  const styles = useFallbackRuleCardStyles();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, active } = useSortable({ id });

  const restoreDeleteTargetAttribute = useRestoreFocusTarget();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const handleDelete = () => {
    setDeleteDialogOpen(false);
    onDelete();
  };

  return (
    <Card
      id={id}
      ref={setNodeRef}
      className={mergeClasses(styles.fallbackRuleCard, className)}
      style={{
        ...style,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...props}
    >
      <span
        ref={setActivatorNodeRef}
        className={styles.fallbackRuleCardHandle}
        style={{ cursor: active ? "grabbing" : "grab" }}
        {...listeners}
      >
        <ReOrderDotsHorizontalRegular />
      </span>
      <Field required label="Fallback Title">
        <Input required value={fallbackRule.title} onChange={(e) => onEdit("title", e.target.value)} />
      </Field>
      <Field required label="Fallback Description">
        <AutosizeTextarea required value={fallbackRule.rule} onChange={(e) => onEdit("rule", e.target.value)} />
      </Field>
      <Field label={<ExampleLanguageLabel label="Fallback Example Language" />}>
        <AutosizeTextarea value={fallbackRule.example} onChange={(e) => onEdit("example", e.target.value)} />
      </Field>
      <Stack align="center" gap="0.25rem" className={styles.fallbackRuleCardActionsRow}>
        <Tooltip relationship="label" content="Swap with default rule">
          <Button
            size="small"
            appearance="primary"
            icon={<ArrowSyncRegular fontSize={12} />}
            aria-label="Swap with default rule"
            onClick={onSwap}
          />
        </Tooltip>
        <Tooltip relationship="label" content="Delete fallback rule">
          <Button
            size="small"
            appearance="primary"
            icon={<DeleteRegular fontSize={12} />}
            aria-label="Delete fallback rule"
            onClick={() => setDeleteDialogOpen(true)}
            {...restoreDeleteTargetAttribute}
          />
        </Tooltip>
      </Stack>
      <DeleteFallbackRuleDialog
        fallbackRule={fallbackRule}
        open={deleteDialogOpen}
        onAccept={handleDelete}
        onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}
      />
    </Card>
  );
}

interface ExampleLanguageLabelProps extends StackProps<"div"> {
  label: string;
}

export function ExampleLanguageLabel({ label, ...props }: ExampleLanguageLabelProps) {
  const styles = useFallbackRuleCardStyles();
  return (
    <Stack inline align="center" gap="0.5rem" {...props}>
      <ArrowEnterLeftRegular fontSize={12} className={styles.exampleLanguageIcon} />
      <span>{label}</span>
    </Stack>
  );
}
